"use client";

import { useState } from "react";

import type { QualityFlags } from "@/hooks/use-quality-flags";
import type { SearchDefaults } from "@/hooks/use-search-defaults";

/* ---------- HelpIcon ---------- */

function HelpIcon({ tooltip }: { tooltip: string }) {
  const [hovered, setHovered] = useState(false);

  return (
    <span
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: "relative", display: "inline-flex", cursor: "help" }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 14,
          height: 14,
          borderRadius: "50%",
          border: "1px solid var(--border)",
          fontSize: 9,
          fontWeight: 700,
          color: hovered ? "var(--text-secondary)" : "var(--text-muted)",
          background: hovered ? "var(--surface-elevated)" : "transparent",
          transition: "all 150ms ease",
          lineHeight: 1,
        }}
      >
        ?
      </span>
      {hovered && (
        <span
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            right: 0,
            width: 220,
            padding: "8px 10px",
            background: "var(--black)",
            color: "white",
            fontSize: 10,
            lineHeight: 1.4,
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 100,
            pointerEvents: "none",
            whiteSpace: "normal",
          }}
        >
          {tooltip}
        </span>
      )}
    </span>
  );
}

/* ---------- ControlHeader ---------- */

function ControlHeader({
  label,
  isNonDefault,
  tooltip,
}: {
  label: string;
  isNonDefault: boolean;
  tooltip: string;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
      <div
        style={{
          width: 4,
          height: 4,
          borderRadius: "50%",
          background: isNonDefault ? "var(--accent-blue)" : "transparent",
          flexShrink: 0,
        }}
      />
      <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", flex: 1 }}>
        {label}
      </span>
      <HelpIcon tooltip={tooltip} />
    </div>
  );
}

/* ---------- QualityFlagGroup ---------- */

interface QualityFlagGroupProps {
  flags: QualityFlags;
  setFlag: <K extends keyof QualityFlags>(
    key: K,
    value: QualityFlags[K]
  ) => void;
  serverDefaults: SearchDefaults;
}

export function QualityFlagGroup({ flags, setFlag, serverDefaults: d }: QualityFlagGroupProps) {
  return (
    <div>
      {/* Result Count */}
      <ResultCountControl flags={flags} setFlag={setFlag} d={d} />

      {/* Pool Multiplier */}
      <PoolMultiplierControl flags={flags} setFlag={setFlag} d={d} />

      {/* Apply button — dispatches event to re-run search with current flags */}
      <div style={{ padding: "12px 0 4px", borderTop: "1px solid rgba(0,0,0,0.06)", marginTop: 8 }}>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new Event("quality-flags-apply"))}
          style={{
            width: "100%",
            padding: "8px 0",
            background: "var(--accent-blue, #2563eb)",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            letterSpacing: "0.02em",
          }}
        >
          Apply &amp; Re-search
        </button>
      </div>
    </div>
  );
}

/* ---------- ResultCountControl ---------- */

function ResultCountControl({
  flags,
  setFlag,
  d,
}: {
  flags: QualityFlags;
  setFlag: <K extends keyof QualityFlags>(key: K, value: QualityFlags[K]) => void;
  d: SearchDefaults;
}) {
  const active = flags.search_top_k ?? d.search_top_k;
  const isNonDefault = flags.search_top_k !== null;

  return (
    <div style={{ padding: "8px 0" }}>
      <ControlHeader
        label="Result Count"
        isNonDefault={isNonDefault}
        tooltip={`Number of search results returned (1-1000). Default: ${d.search_top_k}. Higher values retrieve a larger candidate pool (top_k × multiplier) so scoring has more to work with.`}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 10 }}>
        <input
          type="range"
          min={1}
          max={1000}
          step={1}
          value={active}
          onChange={(e) => {
            const v = Number(e.target.value);
            setFlag("search_top_k", v === d.search_top_k ? null : v);
          }}
          style={{ flex: 1, accentColor: "var(--accent-blue)" }}
        />
        <span
          style={{
            minWidth: 36,
            textAlign: "right",
            fontSize: 11,
            fontWeight: 600,
            fontFamily: "monospace",
            color: isNonDefault ? "var(--accent-blue)" : "var(--text-secondary)",
          }}
        >
          {active}
        </span>
      </div>
    </div>
  );
}

/* ---------- PoolMultiplierControl ---------- */

function PoolMultiplierControl({
  flags,
  setFlag,
  d,
}: {
  flags: QualityFlags;
  setFlag: <K extends keyof QualityFlags>(key: K, value: QualityFlags[K]) => void;
  d: SearchDefaults;
}) {
  const active = flags.candidate_pool_multiplier ?? d.candidate_pool_multiplier;
  const isNonDefault = flags.candidate_pool_multiplier !== null;
  const topK = flags.search_top_k ?? d.search_top_k;
  const poolSize = Math.max(topK * active, 50);

  return (
    <div style={{ padding: "8px 0" }}>
      <ControlHeader
        label="Pool Multiplier"
        isNonDefault={isNonDefault}
        tooltip={`How many candidates vector search returns before quality reranking. Pool = top_k × multiplier. Bigger pool = quality rerank can promote more high-quality candidates. Default: ${d.candidate_pool_multiplier}×.`}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 10 }}>
        <input
          type="range"
          min={2}
          max={d.candidate_pool_multiplier_max}
          step={1}
          value={active}
          onChange={(e) => {
            const v = Number(e.target.value);
            setFlag("candidate_pool_multiplier", v === d.candidate_pool_multiplier ? null : v);
          }}
          style={{ flex: 1, accentColor: "var(--accent-blue)" }}
        />
        <span
          style={{
            minWidth: 44,
            textAlign: "right",
            fontSize: 11,
            fontWeight: 600,
            fontFamily: "monospace",
            color: isNonDefault ? "var(--accent-blue)" : "var(--text-secondary)",
          }}
        >
          {active}x ({poolSize})
        </span>
      </div>
      {poolSize > 8000 && (
        <div style={{ paddingLeft: 10, marginTop: 4, fontSize: 10, color: "#e04040", fontWeight: 600 }}>
          Pool size {poolSize} exceeds HNSW index cap (8000). Results will be capped at ~8000 candidates — lower the multiplier or result count.
        </div>
      )}
      {poolSize <= 8000 && active > 30 && active > d.candidate_pool_multiplier && (
        <div style={{ paddingLeft: 10, marginTop: 4, fontSize: 10, color: "#e8a020", fontStyle: "italic" }}>
          High values (&gt;30x) may drastically slow search
        </div>
      )}
    </div>
  );
}
